import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  Form,
  Table,
  Button,
  Modal,
  Dropdown,
  Col,
  Image,
} from "react-bootstrap";
import userManagementIcon from "../assets/images/icons/user-management-icon.png";
import searchIcon from "../assets/images/icons/search.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { IoFilterOutline } from "react-icons/io5";
import { RiDeleteBin5Line } from "react-icons/ri";
import { GrEdit } from "react-icons/gr";
import { FaEllipsisV } from "react-icons/fa";
import noRecordFound from "../assets/images/no-record.png";
import { jwtDecode } from "jwt-decode";
import clearFilterIcon from "../assets/images/icons/Filters-lines.png";
import { userService } from '../services/userService';
import { ToastContainer, toast } from "react-toastify";

const SuperAdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 5;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    contactNo: "",
    email: "",
  });
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    email: "",
    contactNo: "",
    accountId: "",
    roles: ["SUPERADMIN"]
  });
  const [newUserErrors, setNewUserErrors] = useState({
    username: "",
    password: "",
    email: "",
    contactNo: "",
  });
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);

  const validateEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Enter a valid email";
  const validateUsername = (value) => {
    if (!value) return "Username is required";
    if (value.length < 3) return "Must be at least 3 characters";
    if (!/^[A-Za-z][A-Za-z0-9_.-]{2,19}$/.test(value))
      return "Start with a letter; letters, numbers, _ . - allowed";
    return "";
  };
  const validatePassword = (value) => {
    if (!value) return "Password is required";
    if (value.length < 8) return "Must be at least 8 characters";
    if (!/(?=.*[a-z])/.test(value)) return "Must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/.test(value)) return "Must contain at least one uppercase letter";
    if (!/(?=.*\d)/.test(value)) return "Must contain at least one number";
    if (!/(?=.*[@$!%*?&])/.test(value)) return "Must contain at least one special character";
    return "";
  };
  const validateContact = (value) => {
    if (!value) return "Contact number is required";
    if (!/^\d{10}$/.test(value)) return "Contact number must be exactly 10 digits";
    return "";
  };
  const [userAccountId, setUserAccountId] = useState(null);

  const getUserStatus = (user) => {
    return user && user.enabled ? "Active" : "Inactive";
  };

  useEffect(() => {
    fetchSuperAdminUsers();
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.accountId) {
          setUserAccountId(decodedToken.accountId);
          setNewUser(prev => ({
            ...prev,
            accountId: decodedToken.accountId
          }));
        }
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, []);

  const fetchSuperAdminUsers = async () => {
    try {
      const response = await userService.getAllUsersForSuperAdmin();
      setUsers(response);
    } catch (err) {
      console.error("Failed to fetch super admin users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setUserToEdit(user);
    setEditFormData({
      contactNo: user.contactNo || "",
      email: user.email,
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const [editValidationErrors, setEditValidationErrors] = useState({
    email: "",
    contactNo: "",
  });

  const validateEditEmail = (email) => {
    if (!email) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Enter a valid email address";
    return "";
  };

  const validateEditContact = (contactNo) => {
    if (!contactNo) return "Contact number is required";
    if (!/^\d{10}$/.test(contactNo)) return "Contact number must be exactly 10 digits";
    return "";
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
    
    // Validate on change
    if (name === 'email') {
      setEditValidationErrors(prev => ({ ...prev, email: validateEditEmail(value) }));
    } else if (name === 'contactNo') {
      setEditValidationErrors(prev => ({ ...prev, contactNo: validateEditContact(value) }));
    }
  };

  const saveEditChanges = async () => {
    // Validate before saving
    const errors = {
      email: validateEditEmail(editFormData.email),
      contactNo: validateEditContact(editFormData.contactNo),
    };
    
    setEditValidationErrors(errors);
    
    if (errors.email || errors.contactNo) {
      return;
    }

    try {
      await userService.updateRegisteredUser(userToEdit.id, {
        email: editFormData.email,
        contactNo: editFormData.contactNo,
        roles: userToEdit.roles, 
      });

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userToEdit.id
            ? {
                ...user,
                email: editFormData.email,
                contactNo: editFormData.contactNo,
              }
            : user
        )
      );

      setShowEditModal(false);
      setUserToEdit(null);
      setEditValidationErrors({ email: "", contactNo: "" });
    } catch (err) {
      console.error("Failed to update user:", err);
    }
  };

  const confirmDelete = async () => {
    try {
      await userService.deleteUser(userToDelete.id);

      setUsers((prevUsers) =>
        prevUsers.filter((user) => user.id !== userToDelete.id)
      );
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      console.error("Failed to delete user:", err);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    // Validators
    const validateEmail = (value) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Enter a valid email";
    const validateUsername = (value) => {
      if (!value) return "Username is required";
      if (value.length < 3) return "Must be at least 3 characters";
      if (!/^[A-Za-z][A-Za-z0-9_.-]{2,19}$/.test(value))
        return "Start with a letter; letters, numbers, _ . - allowed";
      return "";
    };
    const validatePassword = (value) => {
      if (!value) return "Password is required";
      if (value.length < 8) return "Must be at least 8 characters";
      if (!/(?=.*[a-z])/.test(value)) return "Must contain at least one lowercase letter";
      if (!/(?=.*[A-Z])/.test(value)) return "Must contain at least one uppercase letter";
      if (!/(?=.*\d)/.test(value)) return "Must contain at least one number";
      if (!/(?=.*[@$!%*?&])/.test(value)) return "Must contain at least one special character";
      return "";
    };
    const validateContact = (value) => {
      if (!value) return "Contact number is required";
      if (!/^\d{10}$/.test(value)) return "Contact number must be exactly 10 digits";
      return "";
    };

    const errors = {
      username: validateUsername(newUser.username),
      password: validatePassword(newUser.password),
      email: validateEmail(newUser.email),
      contactNo: validateContact(newUser.contactNo),
    };
    setNewUserErrors(errors);

    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) {
      return;
    }
    try {
      setIsAddingUser(true);
      const response = await userService.createUser({
        ...newUser,
        accountId: userAccountId,
      });
      if (response) {
        setUsers((prevUsers) => [...prevUsers, response]);
        setShowAddUserModal(false);
        setNewUser({
          username: "",
          password: "",
          email: "",
          contactNo: "",
          accountId: userAccountId,
          roles: ["SUPERADMIN"],
        });
        setNewUserErrors({ username: "", password: "", email: "", contactNo: "" });
      }
    } catch (err) {
      toast.error("Error creating user: " + (err.response?.data?.message || "Creation failed"));
      setShowAddUserModal(false);
      console.error("Failed to create user:", err.response?.data?.message || "Creation failed");
    } finally {
      setIsAddingUser(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      (user.username?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (user.email?.toLowerCase() || "").includes(searchTerm.toLowerCase());

    switch (filterType) {
      case "ACTIVE":
        return matchesSearch && getUserStatus(user) === "Active";
      case "INACTIVE":
        return matchesSearch && getUserStatus(user) === "Inactive";
      default:
        return matchesSearch;
    }
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredUsers.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredUsers.length / recordsPerPage);

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <Container fluid className="ps-0 pb-4 pe-0 overflow-hidden bg-white">
      <ToastContainer position="top-right" autoClose={3000} />
      <Card className="card-nostyle border-0 shadow-none">
        <Card.Body className="ps-0 pe-0">
          <div
            className="d-flex justify-content-between ps-4 pe-4 align-items-center"
            style={{ borderBottom: "1px solid #EAECF0", marginTop:'70px' }}
          >
            <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 pb-3 fs-20 poppins fw-600">
              <Image src={userManagementIcon} className="me-1" width="20" />{" "}
              Super Admin User Management
            </h4>
          </div>

          <div
            className="d-flex justify-content-between align-items-center mb-4 pb-3 pt-3 ps-4 pe-4"
            style={{ borderBottom: "1px solid #EAECF0" }}
          >
            <div className="d-flex gap-3">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search users..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: "250px", paddingRight: "40px" }}
                />
                <Image
                  style={{ right: "15px", top: "12px" }}
                  width={15}
                  className="position-absolute"
                  src={searchIcon}
                />
              </div>

              <Dropdown>
                <Dropdown.Toggle
                  variant="light"
                  className="bg-white cus-dropdown-toggle-show"
                  style={{ borderRadius: "8px", borderColor: "#C2C2C2" }}
                >
                  <IoFilterOutline className="me-2" />
                  {filterType === "ALL"
                    ? "All Users"
                    : filterType === "ACTIVE"
                    ? "Active Users"
                    : "Inactive Users"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    active={filterType === "ALL"}
                    onClick={() => setFilterType("ALL")}
                  >
                    All Users
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={filterType === "ACTIVE"}
                    onClick={() => setFilterType("ACTIVE")}
                  >
                    Active Users
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={filterType === "INACTIVE"}
                    onClick={() => setFilterType("INACTIVE")}
                  >
                    Inactive Users
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {filterType !== "ALL" && (
                <Button
                  variant="light"
                  className=""
                  style={{ padding: "10px 10px" }}
                  onClick={() => setFilterType("ALL")}
                  title="Clear Filter"
                >
                  {/* <FcClearFilters className="fs-16" /> */}
                  <Image src={clearFilterIcon} width={16}/>
                </Button>
              )}
            </div>

            <Button
              variant="success"
              className=""
              style={{ borderRadius: "8px", padding: "10px 10px" }}
              onClick={() => setShowAddUserModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="fs-16 me-1" /> Add User
            </Button>
          </div>

          <div className="ps-4 pe-4">
            <Table
              responsive
              hover
              className="mb-0 table-hover-border table-overflow-unset overflow-unset"
            >
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center">
                      Loading...
                    </td>
                  </tr>
                ) : currentRecords.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">
                      <Image width={100} src={noRecordFound} />
                      <p className="fw-600 text-101828 mb-1 mt-2">
                        No Records Found!
                      </p>
                    </td>
                  </tr>
                ) : (
                  currentRecords.map((user, index) => {
                    const status = getUserStatus(user);
                    return (
                      <tr key={user.id}>
                        <td>{(currentPage - 1) * recordsPerPage + index + 1}</td>
                        <td className="fw-500">{user.username}</td>
                        <td>{user.email}</td>
                        <td>{user.contactNo || "-"}</td>
                        <td>
                          <span
                            className={`badge badge-before position-relative ps-3 bg-${
                              status === "Active" ? "success" : "danger"
                            }`}
                          >
                            {status}
                          </span>
                        </td>
                        <td>
                          <Dropdown className="cust-drop">
                            <Dropdown.Toggle variant="link" id="dropdown-basic">
                              <FaEllipsisV className="text-A8B0B9 fs-12" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                onClick={() => handleEditClick(user)}
                                href="#"
                                className="fs-12 pt-2 pb-2"
                              >
                                <GrEdit className="align-middle fs-12 me-1" /> Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDeleteClick(user)}
                                className="fs-12 pt-2 pb-2"
                                href="#"
                              >
                                <RiDeleteBin5Line className="align-middle fs-14 me-1" />{" "}
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>


            {!loading && currentRecords.length > 0 && (
              <div className="d-flex justify-content-center mt-4">
                <nav>
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        &laquo;
                      </button>
                    </li>
                    {pageNumbers.map((number) => (
                      <li
                        key={number}
                        className={`page-item ${currentPage === number ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(number)}
                        >
                          {number}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>


      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Edit Super Admin User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {userToEdit && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Contact Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="contactNo"
                  placeholder="Enter contact number"
                  className="br-radius-8"
                  value={editFormData.contactNo}
                  onChange={handleEditFormChange}
                  isInvalid={!!editValidationErrors.contactNo}
                />
                <Form.Control.Feedback type="invalid">
                  {editValidationErrors.contactNo}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  className="br-radius-8"
                  value={editFormData.email}
                  onChange={handleEditFormChange}
                  isInvalid={!!editValidationErrors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {editValidationErrors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={saveEditChanges}
            >
              Save Changes
            </Button>
          </div>
        </Modal.Footer>
      </Modal>


      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <h5 className="text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Confirm Delete
          </h5>
          <p className="fw-400 fs-12 mb-1">
            Are you sure you want to delete this super admin user?
          </p>
          {userToDelete && (
            <p className="fw-600 mt-2 mb-0 fs-14">{userToDelete.username}</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-none pt-0 p-3 border-0">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light br-radius-8p"
              className="flex-grow-1"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-grow-1"
              onClick={confirmDelete}
            >
              Yes, Delete
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddUserModal}
        onHide={() => {
          setShowAddUserModal(false);
          setNewUser({
            username: "",
            password: "",
            email: "",
            contactNo: "",
            accountId: userAccountId,
            roles: ["SUPERADMIN"],
          });
        }}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Add New Super Admin User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleAddUser} noValidate>
            <Row>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Username <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter username"
                    value={newUser.username}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewUser({ ...newUser, username: val });
                      setNewUserErrors((prev) => ({ ...prev, username: validateUsername(val) }));
                    }}
                    className="br-radius-8"
                    isInvalid={!!newUserErrors.username}
                    required
                  />
                  <Form.Control.Feedback type="invalid">{newUserErrors.username}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  {/* <div className="position-relative"> */}
                    <Form.Control
                      type={showNewUserPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={newUser.password}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewUser({ ...newUser, password: val });
                        setNewUserErrors((prev) => ({ ...prev, password: validatePassword(val) }));
                      }}
                      className="br-radius-8"
                      style={{ paddingRight: newUserErrors.password ? '60px' : '40px' }}
                      isInvalid={!!newUserErrors.password}
                      required
                    />
                    {/* <span
                      className="position-absolute top-50 translate-middle-y fs-12 text-success"
                      style={{ right: newUserErrors.password ? '30px' : '10px', cursor: 'pointer', zIndex: 10 }}
                      onClick={() => setShowNewUserPassword(prev => !prev)}
                      aria-label={showNewUserPassword ? "Hide password" : "Show password"}
                    >
                      <FontAwesomeIcon icon={showNewUserPassword ? faEyeSlash : faEye} />
                    </span>
                  </div> */}
                  <Form.Control.Feedback type="invalid">{newUserErrors.password}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    value={newUser.email}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewUser({ ...newUser, email: val });
                      setNewUserErrors((prev) => ({ ...prev, email: validateEmail(val) }));
                    }}
                    className="br-radius-8"
                    isInvalid={!!newUserErrors.email}
                    required
                  />
                  <Form.Control.Feedback type="invalid">{newUserErrors.email}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12 form-label ls-spacing-2 text-616161">
                    Contact Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter contact number"
                    value={newUser.contactNo}
                    onChange={(e) => {
                      // const val = e.target.value;
                       const val = e.target.value.replace(/[^0-9]/g, '');
                      setNewUser({ ...newUser, contactNo: val });
                      setNewUserErrors((prev) => ({ ...prev, contactNo: validateContact(val) }));
                    }}
                    className="br-radius-8"
                    isInvalid={!!newUserErrors.contactNo}
                    required
                  />
                  <Form.Control.Feedback type="invalid">{newUserErrors.contactNo}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top p-3">
          <div className="d-flex gap-3 w-75">
            <Button
              variant="light bg-white"
              className="flex-grow-1 br-radius-8"
              onClick={() => setShowAddUserModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              className="flex-grow-1 br-radius-8"
              onClick={handleAddUser}
              disabled={
                isAddingUser ||
                !newUser.username ||
                !newUser.password ||
                !newUser.email ||
                !newUser.contactNo ||
                Object.values(newUserErrors).some(Boolean)
              }
            >
              Add User
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SuperAdminUserManagement;